// 📁 SlotSelector.jsx
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import "../SchedulingStyles/SlotSelector.css";
import FormatUrl from "../../../utils/UrlFormatter";
import moment from "moment";

const SlotSelector = ({
  clinicId,
  practitionerId,
  selectedDate,
  csrfToken,
  bookedSlotIds = [],
  onSelect,
  discipline,
  breakLength
}) => {
  const { t } = useTranslation();
  const [slotsByStation, setSlotsByStation] = useState({});
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [breakEnabled, setBreakEnabled] = useState(true);
  const [loading, setLoading] = useState(false);
  const [noData, setNoData] = useState(false);
  const [activeStation, setActiveStation] = useState("");

  const stationKeys = Object.keys(slotsByStation || {});
  const multipleStations = stationKeys.length > 1;

  const showBreakCheckbox = breakLength && breakLength !== "0";

  useEffect(() => {
    const fetchSlots = async () => {
      if (!clinicId || !practitionerId || !selectedDate || !csrfToken) return;

      setLoading(true);
      setNoData(false);
      setSelectedSlots([]);
      setActiveStation("");

      try {
        const url = FormatUrl(
          `/get-availability-slots/?clinic_id=${clinicId}&practitioner_id=${practitionerId}`
        );

        const response = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": csrfToken,
          },
          credentials: "include",
        });

        const data = await response.json();

        if (data?.slots && typeof data.slots === "object") {
          const stations = {};
          Object.entries(data.slots).forEach(([stationKey, slotList]) => {
            const filtered = slotList.filter((slot) => {
              const isAvailable = slot.status === "AV";
              const isSameDate =
                moment(slot.slot_start_time).format("YYYY-MM-DD") === selectedDate;
              const isFuture = moment(slot.slot_end_time).isAfter(moment());

              return isAvailable && isSameDate && isFuture;
            });

            stations[stationKey] = filtered.map((slot) => ({
              id: String(slot.id),
              label: `${formatTime(slot.slot_start_time)} - ${formatTime(
                slot.slot_end_time
              )}`,
              raw: slot,
            }));
          });

          setSlotsByStation(stations);
          const firstStationKey = Object.keys(stations)[0] || "";
          setActiveStation(firstStationKey);
          setNoData(Object.values(stations).every((list) => list.length === 0));
        } else {
          setNoData(true);
        }
      } catch (error) {
        console.error("Error fetching availability slots:", error);
        setNoData(true);
      } finally {
        setLoading(false);
      }
    };

    fetchSlots();
  }, [clinicId, practitionerId, selectedDate, csrfToken]);

  useEffect(() => {
    if (showBreakCheckbox && breakEnabled && selectedSlots.length > 0) {
      const breakSlot = getBreakSlot(selectedSlots);
      onSelect(selectedSlots, breakEnabled, breakSlot);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [breakEnabled, selectedSlots, onSelect]);
  

  const formatTime = (iso) => {
    const date = new Date(iso);
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const suffix = hours >= 12 ? "PM" : "AM";
    const hour = hours % 12 || 12;
    return `${hour.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")} ${suffix}`;
  };

  const getBreakSlot = (selected) => {
    if (!showBreakCheckbox || !breakEnabled || selected.length === 0) return null;
  
    const lastSelected = selected[selected.length - 1];
    const lastEnd = lastSelected?.raw?.slot_end_time;
  
    return (slotsByStation[activeStation] || []).find(
      (slot) => slot.raw.slot_start_time === lastEnd
    ) || null;
  };
  

  const toggleSlot = (slot) => {
    const selectedIds = selectedSlots.map((s) => s.id);
    const isSelected = selectedIds.includes(slot.id);
  
    const sortedSlots = [...(slotsByStation[activeStation] || [])].sort((a, b) => {
      const aTime = moment(a.label.split(" - ")[0], "hh:mm A");
      const bTime = moment(b.label.split(" - ")[0], "hh:mm A");
      return aTime.diff(bTime);
    });
  
    if (isSelected) {
      const first = selectedSlots[0].id;
      const last = selectedSlots[selectedSlots.length - 1].id;
      if (slot.id !== first && slot.id !== last) return;
  
      const updated = selectedSlots.filter((s) => s.id !== slot.id);
      const breakSlot = getBreakSlot(updated);
      setSelectedSlots(updated);
      onSelect(updated, breakEnabled, breakSlot);
    } else {
      if (selectedSlots.length === 0) {
        const updated = [slot];
        const breakSlot = getBreakSlot(updated);
        setSelectedSlots(updated);
        onSelect(updated, breakEnabled, breakSlot);
      } else {
        const lastSelected = selectedSlots[selectedSlots.length - 1];
        const lastIndex = sortedSlots.findIndex((s) => s.id === lastSelected.id);
        const nextSlot = sortedSlots[lastIndex + 1];
  
        if (nextSlot?.id === slot.id) {
          const updated = [...selectedSlots, slot];
          const breakSlot = getBreakSlot(updated);
          setSelectedSlots(updated);
          onSelect(updated, breakEnabled, breakSlot);
        }
      }
    }
  };
  

  const handleStationChange = (stationKey) => {
    if (stationKey !== activeStation) {
      setSelectedSlots([]);
      setActiveStation(stationKey);
      onSelect([], breakEnabled, null);
    }
  };

  const handleCheckboxChange = (e) => {
    const isChecked = e.target.checked;
    setBreakEnabled(isChecked);
  
    const breakSlot = isChecked && showBreakCheckbox
      ? getBreakSlot(selectedSlots)
      : null;
  
    onSelect(selectedSlots, isChecked, breakSlot);
  };
  

  return (
    <div className="slot-selector-container">
      <div className="slot-selector-header">
        <p>{t("admin_scheduling_new.select_slots_label")}</p>
        {selectedSlots.length > 0 && (
          <span className="slot-count">
            {t("admin_scheduling_new.slots_selected", {
              count: selectedSlots.length,
            })}
          </span>
        )}
      </div>

      {/* Station Tabs */}
      {!loading && multipleStations && (
        <div className="station-tabs">
          {stationKeys.map((stationKey) => (
            <button
              key={stationKey}
              className={`station-tab ${
                stationKey === activeStation ? "active" : ""
              }`}
              onClick={() => handleStationChange(stationKey)}
            >
              {`Room ${stationKey.split("_")[1]}`}
            </button>
          ))}
        </div>
      )}

      {/* Loading / No Data / Slots */}
      {loading && (
        <p className="loading-text">{t("admin_scheduling_new.loading_slots")}</p>
      )}
      {!loading && noData && (
        <p className="no-data-text">
          {t("admin_scheduling_new.no_slots_available")}
        </p>
      )}
      {!loading && !noData && activeStation && (
        <div className="slot-scroll-container">
          {[...(slotsByStation[activeStation] || [])]
            .sort((a, b) => {
              const aTime = moment(a.label.split(" - ")[0], "hh:mm A");
              const bTime = moment(b.label.split(" - ")[0], "hh:mm A");
              return aTime.diff(bTime);
            })
            .map((slot) => {
              const isSelected = selectedSlots.find((s) => s.id === slot.id);
              const isBooked = bookedSlotIds?.includes(slot.id);

              return (
                <div
                  key={slot.id}
                  className={`slot-item ${isSelected ? "selected" : ""} ${
                    isBooked ? "disabled" : ""
                  }`}
                  onClick={() => !isBooked && toggleSlot(slot)}
                >
                  {slot.label}
                </div>
              );
            })}
        </div>
      )}

      {/* Break Slot Checkbox */}
      
      {/* {discipline !== "Physiotherapy" && ( */}
      {showBreakCheckbox && (
        <div className="break-checkbox">
          <label>
            <input
              type="checkbox"
              checked={breakEnabled}
              onChange={handleCheckboxChange}
            />
            {t("admin_scheduling_new.break_checkbox_label")}
          </label>
          <small className="break-note">
            {t("admin_scheduling_new.break_note")}
          </small>
        </div>
      )}
    </div>
  );
};

export default SlotSelector;
